import { useCallback, useMemo, useRef, useState } from 'react';
import { useSurveyRuntimeApi } from '../context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}

/** Horizontal banner uploader: pick an image, then drag it inside a fixed-aspect
 * frame to choose which part shows (CSS object-position — no zoom/crop math). */
export default function SurveyBannerEditor({ surveyId, bannerImage, bannerPosition, onChange }) {
  const { apiFetch } = useSurveyRuntimeApi();
  const { showError } = useSurveyUiToast();
  const fileInputRef = useRef(null);
  const frameRef = useRef(null);
  const dragState = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragPosition, setDragPosition] = useState(null); // live position while dragging
  const position = useMemo(
    () => dragPosition || bannerPosition || { x: 50, y: 50 },
    [dragPosition, bannerPosition],
  );

  const pickFile = useCallback(() => fileInputRef.current?.click(), []);

  const onFileSelected = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (!ALLOWED_TYPES.includes(file.type)) {
        showError('Only PNG, JPEG, and WEBP images are supported.');
        return;
      }
      if (file.size > MAX_BYTES) {
        showError('Image is too large (max 5 MB).');
        return;
      }
      setUploading(true);
      try {
        const dataUrl = await readFileAsDataUrl(file);
        const result = await apiFetch(`/admin/surveys/${surveyId}/banner`, {
          method: 'PUT',
          body: JSON.stringify({
            image_base64: dataUrl,
            mime_type: file.type,
            position: { x: 50, y: 50 },
          }),
        });
        onChange(result);
      } catch (err) {
        showError(getApiErrorMessage(err, 'Failed to upload banner image.'));
      } finally {
        setUploading(false);
      }
    },
    [apiFetch, onChange, showError, surveyId],
  );

  const removeBanner = useCallback(async () => {
    setUploading(true);
    try {
      const result = await apiFetch(`/admin/surveys/${surveyId}/banner`, { method: 'DELETE' });
      onChange(result);
    } catch (err) {
      showError(getApiErrorMessage(err, 'Failed to remove banner image.'));
    } finally {
      setUploading(false);
    }
  }, [apiFetch, onChange, showError, surveyId]);

  const savePosition = useCallback(
    async (next) => {
      try {
        const result = await apiFetch(`/admin/surveys/${surveyId}/banner/position`, {
          method: 'PATCH',
          body: JSON.stringify({ position: next }),
        });
        onChange(result);
      } catch (err) {
        showError(getApiErrorMessage(err, 'Failed to save banner position.'));
      }
    },
    [apiFetch, onChange, showError, surveyId],
  );

  const onPointerDown = useCallback(
    (e) => {
      if (!bannerImage || !frameRef.current) return;
      const rect = frameRef.current.getBoundingClientRect();
      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPos: position,
        width: rect.width,
        height: rect.height,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [bannerImage, position],
  );

  const onPointerMove = useCallback((e) => {
    const drag = dragState.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const x = Math.min(100, Math.max(0, drag.startPos.x - (dx / drag.width) * 100));
    const y = Math.min(100, Math.max(0, drag.startPos.y - (dy / drag.height) * 100));
    setDragPosition({ x, y });
  }, []);

  const onPointerUp = useCallback(
    (e) => {
      const drag = dragState.current;
      dragState.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      if (!drag) return;
      setDragPosition((current) => {
        if (current) void savePosition(current);
        return null;
      });
    },
    [savePosition],
  );

  return (
    <div className="banner-editor">
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={onFileSelected}
      />
      <div
        ref={frameRef}
        className={`banner-frame${bannerImage ? ' has-image' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {bannerImage ? (
          <img
            src={bannerImage}
            alt="Survey banner"
            className="banner-frame-img"
            style={{ objectPosition: `${position.x}% ${position.y}%` }}
            draggable={false}
          />
        ) : (
          <button type="button" className="banner-frame-empty" onClick={pickFile} disabled={uploading}>
            <span className="banner-frame-empty-icon" aria-hidden="true">
              🖼
            </span>
            <span>{uploading ? 'Uploading…' : 'Add banner image'}</span>
          </button>
        )}
      </div>
      {bannerImage && (
        <div className="banner-editor-actions">
          <span className="muted small">Drag the image to reposition it in the frame.</span>
          <div className="banner-editor-buttons">
            <button type="button" className="btn small-btn" onClick={pickFile} disabled={uploading}>
              Replace
            </button>
            <button type="button" className="btn small-btn danger" onClick={removeBanner} disabled={uploading}>
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
